/**
 * Category-specific ops hooks — compute instances, storage business/analytics,
 * and media analytics. Split from operations.ts to keep each file under 200 LOC.
 * All endpoints are employee-gated (opsApi UMA RPT client).
 */
import { useQuery } from '@tanstack/react-query';
import { opsApi } from '@/api/client';

/* ── Instances types ──────────────────────────────────────────────────────── */

/** One compute instance row from GET /internal/ops/services/:id/instances. */
export interface OpsInstanceRow {
  id: string;
  name: string;
  uid: string;
  status: string;
  provider_name: string;
  organization_id: string;
  service_zone_id?: string | null;
  zone_name?: string;
  created_at: string;
  synchronized_at?: string | null;
}

/* ── Storage business types ───────────────────────────────────────────────── */

/** Account-wide storage billing summary (GiB basis, 1024³). */
export interface OpsStorageSummary {
  avg_stored_bytes: number;
  peak_stored_bytes: number;
  /** Billed GiB = peak_stored_bytes / 1024³. */
  gib_month: number;
  raw_bytes?: number;
  deleted_bytes?: number;
  metadata_bytes?: number;
  billable_objects?: number;
  points?: number;
}

/** Per-bucket row in storage business response. */
export interface OpsStorageBucketRow {
  bucket: string;
  avg_stored_bytes: number;
  peak_stored_bytes: number;
  gib_month?: number;
}

/** Full response from GET /internal/ops/services/:id/storage/business. */
export interface OpsStorageBusiness {
  from: string;
  to: string;
  summary: OpsStorageSummary;
  buckets: OpsStorageBucketRow[];
}

/* ── Media analytics types ────────────────────────────────────────────────── */

/** One daily point from media analytics series (field: `ts` = RFC3339). */
export interface OpsMediaPoint {
  ts: string;
  value: number;
}

/** One monthly point from storage analytics series (field: `timestamp` = RFC3339). */
export interface OpsStorageAnalyticsPoint {
  timestamp: string;
  value: number;
}

/** Response from GET /internal/ops/services/:id/media/analytics. */
export interface OpsMediaSeries {
  metric: string;
  from: string;
  to: string;
  series: OpsMediaPoint[];
}

/** Response from GET /internal/ops/services/:id/storage/analytics. */
export interface OpsStorageAnalyticsSeries {
  metric: string;
  granularity: string;
  series: OpsStorageAnalyticsPoint[];
}

/* ── Query key factories ──────────────────────────────────────────────────── */

const opsInstancesKey = (id: string) =>
  ['ops', 'service', id, 'instances'] as const;

const opsStorageBusinessKey = (id: string, from: string, to: string) =>
  ['ops', 'service', id, 'storage', 'business', from, to] as const;

const opsStorageAnalyticsKey = (id: string, from: string, to: string) =>
  ['ops', 'service', id, 'storage', 'analytics', from, to] as const;

const opsMediaAnalyticsKey = (id: string, metric: string, from: string, to: string) =>
  ['ops', 'service', id, 'media', 'analytics', metric, from, to] as const;

/* ── Instances hook ───────────────────────────────────────────────────────── */

/**
 * `GET /internal/ops/services/:id/instances` — DB-only compute instance list.
 * Returns empty array for non-compute services (no error). Employee-gated via opsApi.
 */
export function useOpsServiceInstances(id: string) {
  return useQuery({
    queryKey: opsInstancesKey(id),
    queryFn: async (): Promise<OpsInstanceRow[]> => {
      const res = await opsApi.get<{ results: OpsInstanceRow[] }>(
        `/internal/ops/services/${id}/instances`,
      );
      return res.data.results ?? [];
    },
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

/* ── Storage business hook ────────────────────────────────────────────────── */

/**
 * `GET /internal/ops/services/:id/storage/business` — billing summary + per-bucket
 * breakdown. Uses RFC3339 `from`/`to`. GiB basis (1024³).
 * Envelope: { status: 200, data: OpsStorageBusiness } (response.OK wrapper).
 */
export function useOpsStorageBusiness(
  id: string,
  params: { from: string; to: string },
) {
  return useQuery({
    queryKey: opsStorageBusinessKey(id, params.from, params.to),
    queryFn: async (): Promise<OpsStorageBusiness> => {
      const p = new URLSearchParams({ from: params.from, to: params.to });
      const res = await opsApi.get<{ status: number; data: OpsStorageBusiness }>(
        `/internal/ops/services/${id}/storage/business?${p.toString()}`,
      );
      return res.data.data;
    },
    enabled: Boolean(id) && Boolean(params.from) && Boolean(params.to),
    staleTime: 5 * 60_000,
  });
}

/* ── Storage analytics hook ───────────────────────────────────────────────── */

/**
 * `GET /internal/ops/services/:id/storage/analytics?gte=&lte=` — monthly
 * padded-storage time-series. Params are YYYY-MM-DD (gte/lte).
 * Envelope: { status: 200, data: OpsStorageAnalyticsSeries } (response.OK wrapper).
 */
export function useOpsStorageAnalytics(
  id: string,
  params: { gte: string; lte: string },
) {
  return useQuery({
    queryKey: opsStorageAnalyticsKey(id, params.gte, params.lte),
    queryFn: async (): Promise<OpsStorageAnalyticsSeries> => {
      const p = new URLSearchParams({ gte: params.gte, lte: params.lte });
      const res = await opsApi.get<{ status: number; data: OpsStorageAnalyticsSeries }>(
        `/internal/ops/services/${id}/storage/analytics?${p.toString()}`,
      );
      return res.data.data;
    },
    enabled: Boolean(id) && Boolean(params.gte) && Boolean(params.lte),
    staleTime: 5 * 60_000,
  });
}

/* ── Media analytics hook ─────────────────────────────────────────────────── */

/**
 * `GET /internal/ops/services/:id/media/analytics?metric=&gte=&lte=` — daily
 * media metric time-series. Valid metrics: lms_transcode, vms_transcode, etc.
 * Envelope: { status: 200, data: OpsMediaSeries } (response.OK wrapper).
 */
export function useOpsMediaAnalytics(
  id: string,
  params: { metric: string; gte: string; lte: string },
) {
  return useQuery({
    queryKey: opsMediaAnalyticsKey(id, params.metric, params.gte, params.lte),
    queryFn: async (): Promise<OpsMediaSeries> => {
      const p = new URLSearchParams({ metric: params.metric, gte: params.gte, lte: params.lte });
      const res = await opsApi.get<{ status: number; data: OpsMediaSeries }>(
        `/internal/ops/services/${id}/media/analytics?${p.toString()}`,
      );
      return res.data.data;
    },
    enabled: Boolean(id) && Boolean(params.metric) && Boolean(params.gte) && Boolean(params.lte),
    staleTime: 5 * 60_000,
  });
}
